import React, { useState, useEffect } from 'react';
import {getUserByEmail, sendRecoveryEmail, recoverPassword, ValidateCode} from "../../../../client/users/index";
import ModalSuccess from '../../modals/modalSuccess';
import { MODALS } from '../../../../config/images';

const ModalPassword = ({ showModalPassword, closeModal }) => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(""); 
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);

  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [timer, setTimer] = useState(600);
  const [canResend, setCanResend] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emailRegex.test(email) && email.length > 0;
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  }

  useEffect(() => {
    let interval;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(interval);
            setCanResend(true);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!showModalPassword) return null;

  const handleCheckEmail = async () => {
    
    if (!email.trim()) {
      setError(true);
      setErrorMessage("Por favor ingrese un correo válido.");
      return;
    }

    if (!isValidEmail(email)) {
      setError(true);
      setErrorMessage("Por favor ingrese un correo válido.");
      return;
    }

    setLoading(true);
    setError(false);
    setErrorMessage("");

    try {
      const userResponse = await getUserByEmail(email);

      setUserId(userResponse.data.id);

      const emailResponse = await sendRecoveryEmail(email);

      if (emailResponse.data === true) {
        setEmailSent(true);
        setError(false);
        setErrorMessage("");
        setStep(2);
        setTimer(600); 
        setCanResend(false);

      } else {
        throw new Error('No se pudo enviar el email');
      }

    } catch (error) {
      setError(true); 
      setEmailSent(false);

      if (error.response) {
        const backendMessage = error.response.data?.message || 
                               error.response.data?.error || 
                               error.message;
        setErrorMessage(backendMessage);
      } else if (error.request) {
        setErrorMessage("Error de conexión. Verifique su red e intente nuevamente.");
      } else {
        setErrorMessage(error.message || "Ocurrió un error inesperado.");
      }

    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    
    setLoading(true);
    try {
      const emailResponse = await sendRecoveryEmail(email);
      if (emailResponse.data === true) {
        setTimer(600); 
        setCanResend(false);
        setCodeError("");
      } else {
        throw new Error('No se pudo reenviar el email');
      }
    } catch (error) {
      setCodeError("Error al reenviar el código");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckCode = async () => {
    if (code.length !== 7) {
      setCodeError("El código debe tener 6 dígitos")
      return;
    }

    try {
      const numericCode = code.replace(/\D/g, '');


      const validationResponse = await ValidateCode(numericCode);

      
      if (validationResponse.data && validationResponse.data.userId) {

        setUserId(validationResponse.data.userId);
        setStep(3);
        setCodeError("");
      } else {
        throw new Error('Código inválido o expirado');
      }
    } catch (error) {
    console.error('Error en handleCheckCode:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 429:
          setCodeError(error.response.data?.message || 
                      "Has superado el número máximo de intentos. Intente nuevamente luego de un minuto.");
          break;
        case 400:
          setCodeError(error.response.data?.message || 
                      "Código inválido, expirado o ya utilizado.");
          break;
        case 404:
          setCodeError("Código no encontrado.");
          break;
        default:
          setCodeError("Error al validar el código. Por favor, intente nuevamente.");
      }
    } else if (error.request) {
      setCodeError("Error de conexión. Verifique su red e intente nuevamente.");
    } else {
      setCodeError("Ocurrió un error inesperado.");
    }
  } finally {
    setLoading(false);
  }

  };

  const handleUpdatePassword = async () => {
    try {

      if (!userId) {
        setCodeError('No se encontró el usuario');
      }

      if (!newPassword) {
        setCodeError("Por favor ingrese una nueva contraseña");
        return;
      }

      if (newPassword.length > 15) {
        setCodeError('La contraseña no puede exceder los 15 caracteres');
        return;
      }


      const response = await recoverPassword(userId, { password: newPassword });

      setShowSuccessModal(true);
      
      setTimeout(() => {
        setShowSuccessModal(false);
        closeModalAndReset();
      }, 3000);
    
    } catch (error) {
      console.error('Error en handleUpdatePassword:', error);
      setCodeError((error.response?.data?.message || error.message));    }
  };

  const resetModal = () => {
    setEmail("");
    setCode("");
    setNewPassword("");
    setError(false);
    setCodeError("");
    setUserId(null);
    setStep(1); 
    setTimer(600);
    setCanResend(false);
    setShowPassword(false);
  };
  
  const closeModalAndReset = () => {
    resetModal();
    closeModal();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="lg:w-[35%] md:w-[45%] w-[90%] h-auto bg-white rounded-3xl shadow-lg flex flex-col pb-5  ">
        <div className='flex justify-end'>
          <button
            onClick={closeModalAndReset}
            className="px-4 py-2 rounded-md"
          >
            <img src={MODALS.CLOSE} alt="cerrar"/>
          </button>
        </div>
        <div className='flex flex-col justify-center gap-2 px-8'>
          {step === 1 && (
            <>
              <p className="text-sm text-left text-black font-itcbook">
              Para recuperar su contraseña escriba su correo y le llegará un mensaje con un código de verificación
              </p>
              <p className='text-sm text-left font-itcdemi'>Escriba su correo: <span className="text-red-500">*</span></p>
              <div className='flex flex-col items-center justify-center w-full gap-5'>
                <input
                className='placeholder:font-itcbook w-full flex font-itcbook justify-center items-center outline-none placeholder:text-sm text-sm  placeholder:text-[#6B7280] border-[1px] border-[#D1D5DB] rounded-md p-2'
                placeholder='example@gmail.com'
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(false); 
                  setErrorMessage("");
                }}
                />
                {error && (
                  <p className='w-full text-sm text-left text-red font-itcbook'>
                    {errorMessage || "Ocurrió un error."}
                  </p>
                )}
                <button
                  onClick={handleCheckEmail}
                  disabled={!isValidEmail(email) || loading}
                  className={`text-sm underline  font-itcbold ${
                    !isValidEmail(email) || loading 
                      ? 'text-gray cursor-not-allowed' 
                      : 'text-blue cursor-pointer'
                  }`}
                >
                  Aceptar
                </button>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <p className='text-sm text-left font-itcdemi'>Escriba el código de verificación de 6 dígitos: <span className="text-red-500">*</span></p>
              <input
                className='placeholder:font-itcbook w-full flex font-itcbook justify-center items-center outline-none placeholder:text-sm text-sm placeholder:text-[#6B7280] border-[1px] border-[#D1D5DB] rounded-md p-2'
                placeholder='000-000'
                value={code}
                onChange={(e) => {
                  let rawValue = e.target.value.replace(/\D/g, ''); 
                  if (rawValue.length > 6) rawValue = rawValue.slice(0, 6); 
              
                  const formattedValue =
                    rawValue.length > 3
                      ? rawValue.slice(0, 3) + '-' + rawValue.slice(3)
                      : rawValue;
              
                  setCode(formattedValue);
                  setCodeError('');
                }}
                maxLength={7} 
              />
              {codeError && (
                <p className='w-full text-xs text-left text-red font-itcbook'>
                  {codeError}
                </p>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleResendCode}
                  disabled={!canResend}
                  className={`text-blue font-itcbold text-sm ${canResend ? 'cursor-pointer underline' : 'cursor-default opacity-50'}`}
                >
                  Reenviar código
                </button>
                {!canResend && (
                  <span className="text-sm text-gray-500 font-itcbook">
                    ({formatTime(timer)})
                  </span>
                )}
              </div>
              <button
                onClick={handleCheckCode}
                disabled={code.length !== 7}
                className={`text-sm underline font-itcbold ${
                  code.length !== 7 
                    ? 'text-gray cursor-not-allowed' 
                    : 'text-blue cursor-pointer'
                }`}>
                  Aceptar
              </button>

            </>
          )}

          {step === 3 && (
            <>
              <p className="text-sm text-left text-black font-itcbook">Escriba su nueva contraseña: <span className="text-red-500">*</span></p>
              <div className="relative w-full">
                <span className="absolute inset-y-0 flex items-center pointer-events-none text-gray left-4">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 9V7C17 5.67392 16.4732 4.40215 15.5355 3.46447C14.5979 2.52678 13.3261 2 12 2C10.6739 2 9.40215 2.52678 8.46447 3.46447C7.52678 4.40215 7 5.67392 7 7V9C6.20435 9 5.44129 9.31607 4.87868 9.87868C4.31607 10.4413 4 11.2044 4 12V19C4 19.7956 4.31607 20.5587 4.87868 21.1213C5.44129 21.6839 6.20435 22 7 22H17C17.7956 22 18.5587 21.6839 19.1213 21.1213C19.6839 20.5587 20 19.7956 20 19V12C20 11.2044 19.6839 10.4413 19.1213 9.87868C18.5587 9.31607 17.7956 9 17 9ZM9 7C9 6.20435 9.31607 5.44129 9.87868 4.87868C10.4413 4.31607 11.2044 4 12 4C12.7956 4 13.5587 4.31607 14.1213 4.87868C14.6839 5.44129 15 6.20435 15 7V9H9V7ZM18 19C18 19.2652 17.8946 19.5196 17.7071 19.7071C17.5196 19.8946 17.2652 20 17 20H7C6.73478 20 6.48043 19.8946 6.29289 19.7071C6.10536 19.5196 6 19.2652 6 19V12C6 11.7348 6.10536 11.4804 6.29289 11.2929C6.48043 11.1054 6.73478 11 7 11H17C17.2652 11 17.5196 11.1054 17.7071 11.2929C17.8946 11.4804 18 11.7348 18 12V19Z" fill="black"/>
                    <path d="M12 13.0001C11.798 12.9972 11.6002 13.0691 11.4331 13.2059C11.266 13.3428 11.1377 13.5381 11.0654 13.7655C10.9932 13.993 10.9804 14.2416 11.0289 14.4781C11.0774 14.7146 11.1848 14.9274 11.3366 15.088V16.2C11.3366 16.4122 11.4065 16.6157 11.5309 16.7657C11.6553 16.9157 11.8241 17 12 17C12.1759 17 12.3447 16.9157 12.4691 16.7657C12.5935 16.6157 12.6634 16.4122 12.6634 16.2V15.088C12.8152 14.9274 12.9226 14.7146 12.9711 14.4781C13.0196 14.2416 13.0068 13.993 12.9346 13.7655C12.8623 13.5381 12.734 13.3428 12.5669 13.2059C12.3998 13.0691 12.202 12.9972 12 13.0001Z" fill="#A9ABAD"/>
                  </svg>
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full focus:outline-none h-auto py-3 placeholder:text-black placeholder:text-sm text-sm font-itcbook border-[1px] border-[#D1D5DB] rounded-md pl-12 pr-10"
                  placeholder="Escriba"
                />
                <span 
                  className="absolute inset-y-0 flex items-center cursor-pointer right-4"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.99994 11.7474C7.48358 11.7474 6.25244 10.5163 6.25244 8.99994C6.25244 7.48358 7.48358 6.25244 8.99994 6.25244C10.5163 6.25244 11.7474 7.48358 11.7474 8.99994C11.7474 10.5163 10.5163 11.7474 8.99994 11.7474ZM8.99994 6.37744C7.5538 6.37744 6.37744 7.5538 6.37744 8.99994C6.37744 10.4461 7.5538 11.6224 8.99994 11.6224C10.4461 11.6224 11.6224 10.4461 11.6224 8.99994C11.6224 7.5538 10.4461 6.37744 8.99994 6.37744Z" stroke="#6B7280"/>
                      <path d="M2.10894 10.981L2.10823 10.9799C1.77368 10.4591 1.59131 9.74477 1.59131 9.00292C1.59131 8.26141 1.77354 7.54474 2.10869 7.01963C3.87716 4.26251 6.39653 2.73511 9.00006 2.73511C11.6038 2.73511 14.1229 4.26268 15.8837 7.01926L15.8844 7.02036C16.2189 7.54111 16.4013 8.25545 16.4013 8.9973C16.4013 9.73901 16.219 10.4559 15.8836 10.981C14.1229 13.7376 11.6038 15.2651 9.00006 15.2651C6.38861 15.2651 3.86953 13.7373 2.10894 10.981ZM9.00006 2.86011C6.3643 2.86011 3.90504 4.44214 2.21875 7.08835C1.87235 7.62907 1.71818 8.33206 1.71818 9.00011C1.71818 9.66792 1.87224 10.3706 2.21838 10.9113C3.90468 13.5578 6.36411 15.1401 9.00006 15.1401C11.6358 15.1401 14.0951 13.5581 15.7814 10.9119C16.1278 10.3711 16.2819 9.66816 16.2819 9.00011C16.2819 8.33204 16.1278 7.62902 15.7813 7.08829C14.095 4.44212 11.6358 2.86011 9.00006 2.86011Z" stroke="#6B7280"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.25 7.5C2.89431 8.28073 3.65286 8.95964 4.5 9.51375M4.5 9.51375C5.41237 10.109 6.43096 10.5228 7.5 10.7325C8.49094 10.9229 9.50906 10.9229 10.5 10.7325C11.569 10.5228 12.5876 10.109 13.5 9.51375M4.5 9.51375L3.375 10.875M15.75 7.5C15.1057 8.28073 14.3471 8.95964 13.5 9.51375M13.5 9.51375L14.625 10.875M7.5 10.7318L7.125 12.375M10.5 10.7318L10.875 12.375" stroke="#6B7280" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
              </div>
              {codeError && (
                <p className='w-full text-xs text-left text-red font-itcbook'>
                  {codeError}
                </p>
              )}
              
              <p className='text-[#6B7280] pl-2 text-xs font-itcbook'>
                *La contraseña debe tener entre 6 y 15 caracteres, incluir al menos una letra mayúscula, 
                                una letra minúscula y un número.
                </p>
              <button 
                onClick={handleUpdatePassword}
                disabled={!newPassword}
                className={`text-sm underline font-itcbold ${
                  !newPassword 
                    ? 'text-gray cursor-not-allowed' 
                    : 'text-blue cursor-pointer'
                }`}>
                  Aceptar
              </button>
            </>
          )}
        </div>
      </div>
      <ModalSuccess 
        showModalSuccess={showSuccessModal}
        closeModalSuccess={()=> setShowSuccessModal(false)}
        fill="green"
        text="¡Su contraseña fue cambiada con éxito!"
      />
    </div>
  );
};
export default ModalPassword;
